import { useState, useEffect } from "react";
import {
  Col,
  Row,
  Space,
  Typography,
  Image,
  Form,
  App,
  Upload,
  Result
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BTN_TXT,
  DESC_TXT,
  GLOBAL_CONSTANTS,
  TITLE_TXT,
  INV_TEMP_COL,
  INV_TEMP_DATA,
} from "../../../constants/constants";
import CustomButton from "../../../components/CustomButton/Button";
import CustomInputNumber from "../../../components/CustomInputNumber/InputNumber"
import InvoiceFormatTable from "./InvoiceFormatTable";
import UploadIcon from "../../../assets/icons/upload-icon.svg";
import SuccessImg from "../../../assets/images/success.png"

import Excel from "exceljs";
import { saveAs } from "file-saver";
import { utils, readFile } from "xlsx";
import {
  formatDateTime,
  handleUploadFile,
  parseUnknownDate,
  fundReqRules,
  resolveMaxFundRequiredPct,
  maxFundAllowedAmount,
} from "../../../helpers/helpers";
import { invUpload, invValidate, updateErrorStatus, updateSuccessStatus } from "../../../redux/slices/dealUploadSlice";
import CustomModal from "../../../components/CustomModal/CustomModal"
import FormFooter from "../../../components/CustomForms/FormFooter";
import DealDetailNoSpace from "../../../components/Details/DealDetailNoSpace"
import Step3 from "./Step3";
import Step2 from "./Step2";
import { count } from "../../../redux/slices/AuthSlice";

const { Title, Text } = Typography;

const workSheetName = GLOBAL_CONSTANTS.INVO_FILE;

const UploadFiles = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const location = useLocation();
  const { selectedBusEntId, selectedEnterprise, selectedOrg, tenure } = location.state || {};
  const maxFundRequiredPct = resolveMaxFundRequiredPct(selectedEnterprise?.maxFundRequiredPct);
  const { success, error, validateLoading, invoiceSubmitLoading } = useSelector(
    (state) => state?.dealUpload
  );
  const [financingType, setFinancingType] = useState("LOB1000002");
  const [invoiceData, setInvoiceData] = useState({
    invNum: [],
    invDate: [],
    invDueDate: [],
    invAmt: [],
    totalInvValue: null,
    fundRequired: null,
    invFileUrl: null,
    invZipFileUrl: null,
  });
  const [sheetRows, setSheetRows] = useState([]);
  const [isTempDownloading, setTempDownloading] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const workbook = new Excel.Workbook();
  const isSubmitDisabled =
    !invoiceData.totalInvValue ||
    !invoiceData.fundRequired ||
    !invoiceData.invFileUrl ||
    !invoiceData.invZipFileUrl;

  useEffect(() => {
    if (success) {
      message.success("Summary file validated successfully");
      dispatch(updateSuccessStatus());
    }

    if (error) {
      const errorMessage =
        (error && (error.message || error.response?.message || error)) ||
        "Summary file validation failed";
      message.error(errorMessage);
      dispatch(updateErrorStatus());
    }

    return () => {
      dispatch(updateErrorStatus());
      dispatch(updateSuccessStatus());
    };
  }, [dispatch, message, success, error]);

  const handleDownloadTemp = async () => {
    setTempDownloading(true);
    try {
      const worksheet = workbook.addWorksheet(workSheetName);
      worksheet.columns = INV_TEMP_COL;
      worksheet.getRow(1).font = { bold: true };

      worksheet.columns.forEach((column) => {
        column.width = column.header.length + 5;
        column.alignment = { horizontal: "center" };
      });

      INV_TEMP_DATA.forEach((row) => worksheet.addRow(row));

      worksheet.eachRow({ includeEmpty: false }, (row) => {
        row._cells.forEach((cell) => {
          worksheet.getCell(cell._address).border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      const buf = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buf]), `${workSheetName}.xlsx`);
    } catch (error) {
      message.error(`Excel Download Error: ${error}`);
    } finally {
      workbook.removeWorksheet(workSheetName);
      setTempDownloading(false);
    }
  };

  const handleUploadChange = (info) => {
    if (info.file.status !== "error") {
      const originalFile = info.file.originFileObj;
      const formattedTime = formatDateTime();
      const newName = `Invoice_${formattedTime}_${originalFile.name}`;

      // update file name in UI
      info.file.name = newName;
    }
  };

  const handleBeforeUpload = (file) => {
    const isExcel =
      file.type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "application/vnd.ms-excel";

    if (!isExcel) {
      message.error("You can only upload Excel files!");
      return Upload.LIST_IGNORE;
    }

    const isLt2MB = file.size / 1024 / 1024 < 20;
    if (!isLt2MB) {
      message.error("File must be smaller than 20MB!");
      return Upload.LIST_IGNORE;
    }

    return true; // Allow upload
  };

  const handleCustomRequest = async ({ file, onSuccess, onError }) => {
    const formattedTime = formatDateTime();
    const safeName = file.name.trim().replace(/\s+/g, "_");

    const renamedFile = new File(
      [file],
      `Invoice_${formattedTime}_${safeName}`,
      {
        type: file.type,
      }
    );

    // Clear old data when new file is uploaded
    setSheetRows([]);

    setInvoiceData((prev) => ({
      ...prev,
      invNum: [],
      invDate: [],
      invDueDate: [],
      invAmt: [],
      totalInvValue: null,
      fundRequired: null,
      invFileUrl: null,
    }));

    form.setFieldsValue({
      totalInvValue: undefined,
      fundReq: undefined,
    });

    const reader = new FileReader();
    reader.onload = (event) => {
      const wb = readFile(event.target.result);
      const sheets = wb.SheetNames;
      const options = { dateNF: "dd-MM-yyyy", raw: false };

      if (sheets.length) {
        const rows = utils.sheet_to_json(wb.Sheets[sheets[0]], options);
        setSheetRows(rows);
      }
    };
    reader.readAsArrayBuffer(file);

    try {
      const path = `invoices/${selectedBusEntId}`;
      const { fileUrl } = await handleUploadFile(renamedFile, path);

      setInvoiceData((prev) => ({
        ...prev,
        invFileUrl: fileUrl,
      }));

      form.setFieldsValue({
        // totalInvValue: 0,
        // fundReq: 0,
      });

      // Success snackbar
      message.success("Summary file uploaded successfully");

      onSuccess("ok");
    } catch (err) {
      message.error("Failed to upload summary file");
      onError(err);
    }
  };

  const handleRemoveFile = () => {
    setSheetRows([]);

    setInvoiceData((prev) => ({
      ...prev,
      invNum: [],
      invDate: [],
      invDueDate: [],
      invAmt: [],
      totalInvValue: null,
      fundRequired: null,
      invFileUrl: null,
    }));

    form.setFieldsValue({
      totalInvValue: undefined,
      fundReq: undefined,
    });

    dispatch(updateErrorStatus());
    dispatch(updateSuccessStatus());

    return true;
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    accept: ".xlsx,.xls",
    maxCount: 1,
    beforeUpload: handleBeforeUpload,
    customRequest: handleCustomRequest,
    onChange: handleUploadChange,
    onRemove: handleRemoveFile,
  };

  const handleValidateInvoice = (values) => {
    let invNum = [],
      totInvVal = 0;
    let invAmt = [],
      invDate = [],
      invDueDate = [];

    let hasError = false; // error flag

    const expectedColumns = [
      "Invoice Number",
      "Invoice Date",
      "Invoice Amount",
      "Invoice Due Date",
    ];

    if (sheetRows.length > 0) {
      const uploadedColumns = Object.keys(sheetRows[0] || {});

      const isValidTemplate =
        expectedColumns.length === uploadedColumns.length &&
        expectedColumns.every((col) => uploadedColumns.includes(col));

      if (!isValidTemplate) {
        message.error(
          "Invalid file format. Please upload the downloaded invoice template only."
        );
        return;
      }
    }

    if (sheetRows.length) {
      for (const obj of sheetRows) {
        const formattedObj = {};
        if (!Object.keys(obj).some((k) => obj[k])) {
          message.error("Uploaded file is not valid");
          hasError = true;
          break;
        }

        Object.entries(obj).forEach(([k, v]) => {
          if (k === "Invoice Number") formattedObj.invoiceNumber = v;
          if (k === "Invoice Date") formattedObj.invoiceDate = v;
          if (k === "Invoice Amount") formattedObj.invoiceAmount = v;
          if (k === "Invoice Due Date") formattedObj.invoiceDueDate = v;
        });

        const { invoiceNumber, invoiceAmount, invoiceDate, invoiceDueDate } =
          formattedObj;

        const newinvoiceDate = parseUnknownDate(invoiceDate);
        const newinvoiceDueDate = parseUnknownDate(invoiceDueDate);

        if (new Date(newinvoiceDueDate) < new Date(newinvoiceDate)) {
          message.error("Invoice due date must be greater than invoice date");
          hasError = true;
          break;
        }

        const todayCalendarStr = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split("T")[0];

        if (newinvoiceDate > todayCalendarStr) {
          message.error("Invoice date should not be in the future");
          hasError = true;
          break;
        }

        totInvVal += Number(invoiceAmount);
        invNum.push(invoiceNumber);
        invDate.push(newinvoiceDate);
        invDueDate.push(newinvoiceDueDate);
        invAmt.push(invoiceAmount);
      }

      if (hasError) return; // prevent API call

      const duplicates = invNum.filter((num, idx) => invNum.indexOf(num) !== idx);
      if (duplicates.length > 0) {
        message.error(`Duplicate Invoice numbers found in the uploaded file: ${[...new Set(duplicates)].join(", ")}`);
        return;
      }

      // Auto-calc max fund from credit-limit % (integer math avoids float floor bugs, e.g. 700000*0.7)
      const autoFundReq = maxFundAllowedAmount(totInvVal, maxFundRequiredPct);

      const finalFundReq = autoFundReq;

      form.setFieldsValue({
        totalInvValue: totInvVal,
        fundReq: finalFundReq,
      });

      setInvoiceData((prev) => ({
        ...prev,
        invNum,
        invDate,
        invDueDate,
        invAmt,
        totalInvValue: totInvVal,
        fundRequired: finalFundReq,
      }));

      const payload = {
        busEntId: selectedBusEntId,
        invoiceNumber: invNum,
        invoiceDate: invDate,
        invoiceAmt: invAmt,
        invoiceDueDate: invDueDate,
        fundRequired: finalFundReq,
      };

      dispatch(invValidate(payload));
    }
  };

  const handleInvoicesSubmit = async () => {
    try {
      await form.validateFields(["fundReq"]);

      const fundReq = form.getFieldValue("fundReq");

      const payload = {
        busEntId: selectedBusEntId,
        tenure: Number(tenure),
        totalInvoiceValue: invoiceData.totalInvValue,
        fundRequired: fundReq,
        lineOfBusId: financingType,
        uploadFiles: {
          invoicesummary: {
            invoiceNumber: invoiceData.invNum,
            invoiceDate: invoiceData.invDate,
            invoiceAmt: invoiceData.invAmt,
            invoiceDueDate: invoiceData.invDueDate,
            docUrl: invoiceData.invFileUrl,
          },
          invoiceZipFile: {
            docUrl: invoiceData.invZipFileUrl,
          },
        },
      };

      const result = await dispatch(invUpload(payload));
      if (result.payload.statusCode === 201) {
        setSuccessModalOpen(true)
        setTimeout(() => {
          navigate("/deals-management");
        }, 2000);
        dispatch(count("ALL"));
      }
    } catch (error) {
      return;
    }
  };

  return (
    <div className="upload-files-container">
      <Title level={3} style={{ margin: 0, fontSize: 24 }}>
        {TITLE_TXT.INV_UPLOAD}
      </Title>

      <div className="bus-ent-details-container">
        <Row gutter={[0, 16]} justify="space-between">
          <Col>
            <Space direction="vertical" size="middle">
              <DealDetailNoSpace
                label={GLOBAL_CONSTANTS.INVO_FOR_ENT}
                value={selectedEnterprise?.entName || GLOBAL_CONSTANTS.NA}
              />
              <DealDetailNoSpace
                label={GLOBAL_CONSTANTS.INVO_FOR_BUS}
                value={selectedOrg?.orgName || GLOBAL_CONSTANTS.NA}
              />
            </Space>
          </Col>
          <Col>
            <DealDetailNoSpace
              label={GLOBAL_CONSTANTS.TENURE}
              value={tenure || GLOBAL_CONSTANTS.NA}
            />
          </Col>
        </Row>
      </div>

      <Row gutter={[0, 16]} style={{ marginTop: 30 }} justify="space-between">
        <Col>
          <Space direction="vertical" size="small">
            <Title level={5} style={{ margin: 0 }}>
              {TITLE_TXT.INV_UPLOAD_STEP_1}
            </Title>
            <Text style={{ color: "#40566D", fontSize: 14 }}>
              {DESC_TXT.INV_UPLOAD_STEP_1}
            </Text>
          </Space>
        </Col>
        <Col>
          <CustomButton
            type="primary"
            style={{ padding: 24 }}
            onClick={handleDownloadTemp}
            loading={isTempDownloading}
          >
            {BTN_TXT.DWNLD_SUM_TEMP}
          </CustomButton>
        </Col>
      </Row>

      <InvoiceFormatTable />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleValidateInvoice}
        initialValues={{
          // totalInvValue: 0,
          // fundReq: 0,
        }}
        className="invoice-upload-form"
        style={{ marginTop: 40 }}
      >
        <Row gutter={[24, 16]}>
          <Col xs={24} lg={12}>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Form.Item>
                  <Upload {...uploadProps} style={{ width: "100%" }}>
                    <CustomButton
                      color="primary"
                      variant="outlined"
                      block
                      icon={
                        <Image preview={false} src={UploadIcon} alt="upload" />
                      }
                      className="upload-validate-btn"
                    >
                      {BTN_TXT.UPLOAD_SUM_FILE}
                    </CustomButton>
                  </Upload>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item>
                  <CustomButton
                    color="danger"
                    variant="outlined"
                    htmlType="submit"
                    loading={validateLoading}
                    disabled={!invoiceData.invFileUrl}
                    block
                    className="upload-validate-btn"
                  >
                    {BTN_TXT.VALIDATE_SUM}
                  </CustomButton>
                </Form.Item>
              </Col>
            </Row>
          </Col>

          <Col xs={24} lg={12}>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <CustomInputNumber
                  label="Total Value(auto-calculated)"
                  name="totalInvValue"
                  controls={false}
                  disabled
                />
              </Col>
              <Col xs={24} md={12}>
                <CustomInputNumber
                  label="Funding Amount"
                  name="fundReq"
                  controls={false}
                  rules={fundReqRules(invoiceData.totalInvValue || 0, maxFundRequiredPct)}
                />
              </Col>
            </Row>

          </Col>
        </Row>
      </Form>

      <Step2 selectedBusEntId={selectedBusEntId} setInvoiceData={setInvoiceData} handleUploadChange={handleUploadChange} />

      <Step3 financingType={financingType} setFinancingType={setFinancingType} />

      <FormFooter
        onCancelClick={() => {
          navigate(-1);
          form.resetFields();
        }}
        onSubmitClick={handleInvoicesSubmit}
        btnType="invoice"
        disabled={isSubmitDisabled}
        submitLoading={invoiceSubmitLoading}
        btnName={BTN_TXT.SUBMIT}
        error={error}
      />

      {successModalOpen && (
        <CustomModal
          open={successModalOpen}
          onCancel={() => setSuccessModalOpen(false)}
          centered
          content={
            <>
              <Result
                icon={<Image src={SuccessImg} preview={false} alt="Success" />}
                title={<Title level={4}>{TITLE_TXT.DOC_SUB_SUC}</Title>}
                subTitle={<Text style={{ color: "#40566D", fontSize: 14 }}>{DESC_TXT.DOC_SUB_SUC}</Text>}
              />
            </>
          }
        />
      )}
    </div>
  );
};

export default UploadFiles;
